// =============================================================================
// ProfileSection.js — Phase 82
//
// Wired to Supabase `profiles` table (first_name, last_name, date_of_birth,
// country, city, currency). Currency dropdown is locked once the user has any
// transactions or accounts (defence-in-depth against ambiguous historic
// amounts). Email is displayed read-only from Supabase auth.
// =============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { CURRENCIES } from '@/lib/format';
import {
  SectionHeader, SettingsCard, PrimaryButton, TextInput, FieldLabel, SavedFlash, SelectInput,
} from './primitives';
import DangerZone from './DangerZone';

function ageFromDOB(dobStr) {
  if (!dobStr) return null;
  const d = new Date(dobStr);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years -= 1;
  if (years < 0 || years > 130) return null;
  return years;
}

export default function ProfileSection() {
  const { user, profile, refreshProfile } = useAuth();
  const [form, setForm] = useState({
    first_name: '', last_name: '', date_of_birth: '', country: '', city: '', currency: 'INR',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [hasDataLoaded, setHasDataLoaded] = useState(false);

  // Hydrate from profile
  useEffect(() => {
    if (!profile) return;
    setForm({
      first_name:    profile.first_name || '',
      last_name:     profile.last_name || '',
      date_of_birth: (profile.date_of_birth || '').toString().slice(0, 10),
      country:       profile.country || '',
      city:          profile.city || '',
      currency:      profile.currency || 'INR',
    });
  }, [profile]);

  // Currency lock probe
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [t, a] = await Promise.all([
          supabase.from('transactions').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
          supabase.from('accounts').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        ]);
        if (cancelled) return;
        setHasData(((t.count || 0) + (a.count || 0)) > 0);
        setHasDataLoaded(true);
      } catch {
        if (!cancelled) setHasDataLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const derivedAge = useMemo(() => ageFromDOB(form.date_of_birth), [form.date_of_birth]);

  const initial = (form.first_name || form.last_name || user?.email || '?').charAt(0).toUpperCase();

  const save = async () => {
    if (!user) return;
    setSaving(true);
    const combined = [form.first_name, form.last_name].filter(Boolean).join(' ').trim();
    const payload = { ...form, name: combined || null };
    if (hasData && profile?.currency && payload.currency !== profile.currency) {
      payload.currency = profile.currency;
    }
    if (!payload.date_of_birth) payload.date_of_birth = null;
    payload.city    = (payload.city || '').trim();
    payload.country = (payload.country || '').trim();
    const { error } = await supabase.from('profiles').update(payload).eq('id', user.id);
    setSaving(false);
    if (error) {
      toast.error(error.message || 'Could not save changes.');
      return;
    }
    await refreshProfile?.();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    toast.success('Profile updated.');
  };

  return (
    <div className="space-y-5" data-testid="settings-profile-section">
      <SectionHeader title="Profile" subtitle="Manage your personal details and how 2120 addresses you." />

      {/* Avatar + name summary */}
      <SettingsCard className="px-6 py-6">
        <div className="flex items-center gap-5 mb-6 pb-6 border-b border-border/60">
          <div className="w-16 h-16 rounded-full bg-evergreen flex items-center justify-center flex-shrink-0">
            <span className="font-display text-2xl font-medium text-white">{initial}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-charcoal truncate">
              {[form.first_name, form.last_name].filter(Boolean).join(' ') || user?.email || 'Your name'}
            </p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel label="First name" />
            <TextInput
              value={form.first_name}
              onChange={(v) => setForm({ ...form, first_name: v })}
              placeholder="Your first name"
              testId="settings-first-name-input"
            />
          </div>
          <div>
            <FieldLabel label="Last name" />
            <TextInput
              value={form.last_name}
              onChange={(v) => setForm({ ...form, last_name: v })}
              placeholder="Your last name"
              testId="settings-last-name-input"
            />
          </div>
          <div>
            <FieldLabel label="Email address" />
            <TextInput value={user?.email || ''} onChange={() => {}} disabled />
            <p className="text-[11px] text-muted-foreground/70 mt-1.5">Email is managed through your sign-in provider.</p>
          </div>
          <div>
            <FieldLabel
              label={`Date of birth${derivedAge != null ? ' · Age ' + derivedAge : ''}`}
              hint="Used only to age-adjust projections and retirement timing."
            />
            <TextInput
              type="date"
              value={form.date_of_birth}
              onChange={(v) => setForm({ ...form, date_of_birth: v })}
              testId="settings-dob-input"
            />
          </div>
          <div>
            <FieldLabel label="Country" />
            <TextInput
              value={form.country}
              onChange={(v) => setForm({ ...form, country: v })}
              placeholder="e.g. India, United States"
              testId="settings-country-input"
            />
          </div>
          <div>
            <FieldLabel label="City" />
            <TextInput
              value={form.city}
              onChange={(v) => setForm({ ...form, city: v })}
              placeholder="e.g. Mumbai, Sydney"
              testId="settings-city-input"
            />
          </div>
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <label className="text-sm font-medium text-charcoal">Default currency for new entries</label>
                {hasData && <Lock className="h-3 w-3 text-muted-foreground" aria-label="Locked" />}
              </div>
              {hasDataLoaded && hasData && <span className="text-[10px] text-muted-foreground">Locked</span>}
            </div>
            <SelectInput
              value={form.currency}
              onChange={(v) => setForm({ ...form, currency: v })}
              disabled={hasData}
              options={CURRENCIES.map((c) => ({ value: c.code, label: `${c.symbol} ${c.code}` }))}
              testId={hasData ? 'settings-currency-select-locked' : 'settings-currency-select'}
            />
            <p className="text-[11px] text-muted-foreground/70 mt-1.5 leading-relaxed">
              Used as the default when you add a new transaction or account. To change your headline display currency, use Region & Currency below.
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <PrimaryButton onClick={save} disabled={saving} testId="settings-profile-save">
            {saving ? 'Saving…' : 'Save changes'}
          </PrimaryButton>
          <SavedFlash visible={saved} />
        </div>
      </SettingsCard>

      {/* Sign-in methods (informational, read-only for now) */}
      <SettingsCard className="px-6 py-5">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-4">Sign-in method</p>
        <div className="flex items-center gap-3 py-2">
          <div className="w-8 h-8 rounded-lg bg-offwhite border border-border flex items-center justify-center text-xs font-semibold text-muted-foreground/70">
            \u2709
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-charcoal">Email &amp; password</p>
            <p className="text-xs text-emerald-700 dark:text-emerald-400">Active</p>
          </div>
        </div>
      </SettingsCard>

      {/* Phase 84.23.5 — Danger Zone (delete all data). Kept at the
          bottom of Profile so it's discoverable but visually gated. */}
      <DangerZone />
    </div>
  );
}
